import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { CurrentUser, UserRole } from '../lib/types';

export type ProjectMemberRole = 'owner' | 'admin' | 'contributor' | 'viewer';

export interface AdminUser extends CurrentUser {}

export interface ProjectMemberRow {
  user_id: string;
  email: string;
  name: string;
  role: ProjectMemberRole;
  joined_at: string;
}

// ─── Users ───────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; name: string; role: UserRole; password: string }) =>
      api<{ user: AdminUser }>('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      name?: string;
      role?: UserRole;
      active?: boolean;
      password?: string;
    }) =>
      api<{ user: AdminUser }>(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ user: AdminUser }>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

// ─── Project members ─────────────────────────────────────────────────

export function useProjectMembers(slug: string | undefined) {
  return useQuery({
    queryKey: ['project-members', slug],
    queryFn: () =>
      api<{ members: ProjectMemberRow[] }>(`/api/admin/projects/${slug}/members`).then((r) => r.members),
    enabled: Boolean(slug),
  });
}

export function useAddProjectMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role?: ProjectMemberRole }) =>
      api<{ ok: boolean }>(`/api/admin/projects/${slug}/members`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-members', slug] }),
  });
}

export function useUpdateProjectMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectMemberRole }) =>
      api<{ ok: boolean }>(`/api/admin/projects/${slug}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-members', slug] }),
  });
}

export function useRemoveProjectMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ ok: boolean }>(`/api/admin/projects/${slug}/members/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-members', slug] }),
  });
}
